/**
 * The Projects page's filters and sort (pages/Projects.jsx), kept apart so each can be tested on its own.
 * - Every filter narrows the list (they combine: AND); the search matches a project's name, description, owner, id
 *   and workspaces, in the browser (the whole list is loaded anyway), ignoring case.
 * - The filters and the sort are remembered in this browser (`projects_filters`), so a reload or a refresh keeps them.
 * - One sort for the grid and the table: the Sort by presets, or a column header of the table (`{ key, dir }`).
 */
import { compareValues } from '../components/table/DataTable';
import { lastActivityMs } from './projectRecency';

export const FILTERS_KEY = 'projects_filters';
// Before 2026-10-30 only "Favorites" was remembered, under its own key.
const OLD_FAVORITES_KEY = 'projects_favorites_only';

export const DEFAULT_FILTERS = Object.freeze({
  search: '',
  creator: 'all',
  workspace: 'all',
  runningOnly: false,
  favoritesOnly: false,
  sort: Object.freeze({ key: 'visited', dir: 'desc' }),
});

// The Sort by presets; a sort set from a column header that is none of them shows as that column.
export const SORT_PRESETS = [
  { id: 'visited', label: 'Last visited', sort: { key: 'visited', dir: 'desc' } },
  { id: 'name', label: 'Name', sort: { key: 'name', dir: 'asc' } },
  { id: 'created', label: 'Created', sort: { key: 'created', dir: 'desc' } },
  { id: 'running', label: 'Running first', sort: { key: 'status', dir: 'desc' } },
];

export function presetOf(sort) {
  return SORT_PRESETS.find((preset) => preset.sort.key === sort?.key && preset.sort.dir === sort?.dir) || null;
}

export function loadFilters(storage = globalThis.localStorage) {
  let saved = {};
  try {
    saved = JSON.parse(storage?.getItem(FILTERS_KEY) || '{}') || {};
    if (!('favoritesOnly' in saved) && storage?.getItem(OLD_FAVORITES_KEY) === 'true') { saved.favoritesOnly = true; }
  } catch (_error) { saved = {}; }
  const sort = saved.sort && typeof saved.sort.key === 'string' && ['asc', 'desc'].includes(saved.sort.dir)
    ? { key: saved.sort.key, dir: saved.sort.dir } : DEFAULT_FILTERS.sort;
  return {
    search: typeof saved.search === 'string' ? saved.search : '',
    creator: typeof saved.creator === 'string' && saved.creator ? saved.creator : 'all',
    workspace: typeof saved.workspace === 'string' && saved.workspace ? saved.workspace : 'all',
    runningOnly: saved.runningOnly === true,
    favoritesOnly: saved.favoritesOnly === true,
    sort,
  };
}

export function saveFilters(filters, storage = globalThis.localStorage) {
  try { storage?.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch (_error) { /* kept for this visit only */ }
}

/** How many filters narrow the list now (the sort is not one). */
export function activeFilterCount(filters) {
  return [
    filters.search.trim() !== '',
    filters.creator !== 'all',
    filters.workspace !== 'all',
    filters.runningOnly,
    filters.favoritesOnly,
  ].filter(Boolean).length;
}

/** Whether `project` matches every word of `query` in any of `fields` (strings), ignoring case. */
export function matchesSearch(query, fields) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) { return true; }
  const text = fields.filter(Boolean).join(' ').toLowerCase();
  return words.every((word) => text.includes(word));
}

const ms = (value) => {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};

/**
 * `projects` sorted on `sort` (a stable sort: ties go by name, then id). `values` maps a sort key to the value of a
 * project (`(project) => value`); the page passes the ones that need its state (status, owner…).
 */
export function sortProjects(projects, sort, values = {}) {
  const pick = {
    name: (p) => p.name || '',
    visited: (p) => lastActivityMs(p) || null,
    created: (p) => ms(p.createdAt),
    description: (p) => p.description || p.info?.description || '',
    ...values,
  }[sort?.key];
  const byName = [...projects].sort((a, b) => compareValues(a.name || '', b.name || '') || String(a._id).localeCompare(String(b._id)));
  if (!pick) { return byName; }
  return byName
    .map((project, index) => ({ project, index, value: pick(project) }))
    .sort((a, b) => compareValues(a.value, b.value, sort.dir) || a.index - b.index)
    .map((entry) => entry.project);
}
